// studentHomeDerive.js
// Pure derivation functions behind useStudentHomeData — no React, no network,
// deterministic given `now`. See docs/superpowers/specs/2026-09-16-home-redesign.md §4.
import { HOME } from "../homeTheme";
import { formatDue, weekdayShort } from "../homeTime";
import { CHECK_IN_TITLES } from "../launcherCatalog";

// Backend get-engagement `accuracy` is a 0-100 percentage; WeekStrip expects a 0-1 fraction.
const BACKEND_ACCURACY_SCALE = 100;

const identity = (s) => s;

const toValidDate = (iso) => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/**
 * Weekly summary from `get-engagement?view_type=daily&limit=30` rows.
 * `now` bounds the "last 7 calendar days" window so the result is deterministic.
 */
export function deriveWeek(rows, now) {
  const list = Array.isArray(rows) ? rows : [];
  const windowDates = new Set();
  for (let i = 0; i < 7; i += 1) {
    windowDates.add(new Date(now.getTime() - i * 86400000).toISOString().split("T")[0]);
  }

  const activeDays = new Set();
  let secondsTotal = 0;
  let quizzes = 0;
  let weightedAccuracySum = 0;
  let accuracyWeight = 0;

  list.forEach((row) => {
    const dateOnly = String(row?.period || "").split("T")[0];
    if (!windowDates.has(dateOnly)) return;

    const hasActivity =
      Number(row.clicks) > 0 ||
      Number(row.quiz_attempts) > 0 ||
      Number(row.pwo_launches) > 0 ||
      Number(row.video_plays) > 0 ||
      Number(row.ai_questions) > 0;
    if (hasActivity) activeDays.add(dateOnly);

    secondsTotal += Number(row.time_active_seconds) || 0;

    const attempts = Number(row.quiz_attempts) || 0;
    quizzes += attempts;
    if (attempts > 0 && row.accuracy != null) {
      weightedAccuracySum += row.accuracy * attempts;
      accuracyWeight += attempts;
    }
  });

  return {
    learningDays: activeDays.size,
    goalDays: HOME.weeklyGoalDays,
    minutes: Math.round(secondsTotal / 60),
    quizzes,
    accuracy:
      accuracyWeight > 0
        ? Math.min(1, Math.max(0, weightedAccuracySum / accuracyWeight / BACKEND_ACCURACY_SCALE))
        : null,
  };
}

const isCheckInTest = (test) =>
  String(test?.test_kind || "").toLowerCase() === "assessment" &&
  CHECK_IN_TITLES.some((prefix) => String(test?.title || "").startsWith(prefix));

const tagForTest = (test, now, t, lang) => {
  const endTime = toValidDate(test.end_time);
  const startTime = toValidDate(test.start_time);

  if (endTime && endTime.getTime() < now.getTime()) {
    return { label: t("Overdue"), tone: "bad" };
  }
  if (isCheckInTest(test)) {
    return { label: t("Check-in"), tone: "info" };
  }

  const dueDate = endTime || startTime;
  if (dueDate) {
    const daysUntil = (dueDate.getTime() - now.getTime()) / 86400000;
    return { label: weekdayShort(dueDate.toISOString(), lang), tone: daysUntil <= 2 ? "bad" : "warn" };
  }

  // Neither date is set: not covered explicitly by the spec's due-soon tag
  // rules, so fall back to a neutral, non-misleading tag.
  return { label: t("Open"), tone: "info" };
};

const subtitleForTest = (test, t, lang) => {
  const base = test.end_time
    ? `${t("Due")} ${formatDue(test.end_time, t, lang)}`
    : test.start_time
      ? `${t("Opens")} ${formatDue(test.start_time, t, lang)}`
      : formatDue(null, t);
  return test.duration_minutes ? `${base} · ${test.duration_minutes} ${t("min")}` : base;
};

/**
 * Merge availableTests + upcomingTests, drop already-submitted tests, keep
 * only tests due/opening soon (or with no end date at all), sort, and cap.
 */
export function deriveDueSoon({ availableTests, upcomingTests, myTestResults, now, t = identity, lang }) {
  const submittedIds = new Set(
    (Array.isArray(myTestResults) ? myTestResults : []).map((r) => r?.test_id ?? r?.id)
  );

  const merged = [];
  const seenIds = new Set();
  [...(availableTests || []), ...(upcomingTests || [])].forEach((test) => {
    if (!test || seenIds.has(test.id)) return;
    seenIds.add(test.id);
    merged.push(test);
  });

  const dueSoonMs = HOME.dueSoonDays * 86400000;
  const kept = merged.filter((test) => {
    if (submittedIds.has(test.id)) return false;

    const endTime = toValidDate(test.end_time);
    const startTime = toValidDate(test.start_time);
    const endOk = !endTime || endTime.getTime() - now.getTime() <= dueSoonMs;
    const startOk =
      startTime && startTime.getTime() > now.getTime() && startTime.getTime() - now.getTime() <= dueSoonMs;
    return endOk || startOk;
  });

  kept.sort((a, b) => {
    const aTime = toValidDate(a.end_time ?? a.start_time)?.getTime() ?? Infinity;
    const bTime = toValidDate(b.end_time ?? b.start_time)?.getTime() ?? Infinity;
    return aTime - bTime;
  });

  return kept.slice(0, HOME.maxDueSoon).map((test) => ({
    test,
    tag: tagForTest(test, now, t, lang),
    subtitle: subtitleForTest(test, t, lang),
  }));
}

const SUGGESTION_TAGS = {
  resume: { label: "Pick up", tone: "info" },
  practice: { label: "Keep at it", tone: "warn" },
  take_test: { label: "Assigned", tone: "bad" },
  review: { label: "Review", tone: "warn" },
};

/** Map `/recommendations` items to display tags, dropping resume items when a Continue card already shows one. */
export function mapSuggestions(recommendations, { hasResume = false } = {}) {
  const list = Array.isArray(recommendations) ? recommendations : [];
  return list
    .filter((rec) => !(hasResume && rec?.action === "resume"))
    .map((rec) => ({
      tag: SUGGESTION_TAGS[rec?.action] || { label: rec?.action, tone: "info" },
      title: rec?.title,
      subtitle: rec?.subtitle,
      action: rec?.action,
      action_payload: rec?.action_payload,
    }));
}
